// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, Combobox, Input, Radio, toast } from '@node-space/ui-components'
import { useUpdatePreProcessedRecordMutation } from 'hooks/mutations/useUpdatePreProcessedRecordMutation'
import { useFilteredCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useSetFiatRecordToEditErrors } from 'pages/MassPayouts/hooks/useSetFiatRecordToEditErrors'
import { updateFiatPayoutSchema } from 'pages/MassPayouts/schemas/updateFiatPayoutSchema'
import {
  PreProcessedPayouts,
  UpdateFiatPayoutFormPayload,
  UpdateFiatPayoutFormPayloadKeys,
} from 'pages/MassPayouts/types'
import { getEditBeneficiaryPayload, isFiatB2CPayment } from 'pages/MassPayouts/utils'
import {
  BaseErrorResponse,
  BeneficiaryTransferDestination,
  BeneficiaryType,
} from 'types/beneficiaries'
import { mapToItems } from 'utils/utils'

interface FiatPayoutEditRecordFormProps {
  record: PreProcessedPayouts
  setIsEditPayoutModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  setPreProcessedRecordToUpdate: React.Dispatch<React.SetStateAction<PreProcessedPayouts>>
  walletCurrency: string
  isDeletingPreProcessedRecord: boolean
  onDeletePreProcessedRecordRequest: (reference: string) => void
}

export const FiatPayoutEditRecordForm = ({
  record,
  setIsEditPayoutModalVisible,
  setPreProcessedRecordToUpdate,
  walletCurrency,
  onDeletePreProcessedRecordRequest,
  isDeletingPreProcessedRecord,
}: FiatPayoutEditRecordFormProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const formSchema = updateFiatPayoutSchema(t)
  const { control, formState, register, setValue, trigger, clearErrors, setError } =
    useForm<UpdateFiatPayoutFormPayload>({
      resolver: yupResolver(formSchema),
    })

  const {
    mutate: mutateUpdatePreProcessedRecord,
    isPending: isLoadingUpdatePreProcessedRecord,
    isError: isError,
  } = useUpdatePreProcessedRecordMutation()
  const { beneficiary: beneficiaryDetails } = record?.details ?? {}

  const [
    accountNumber,
    amount,
    bankCode,
    bankCountry,
    type,
    businessName,
    dateOfBirth,
    firstName,
    lastName,
    paymentReference,
    transferDestination,
  ] = useWatch({
    control,
    name: [
      'accountNumber',
      'amount',
      'bankCode',
      'bankCountry',
      'type',
      'businessName',
      'dateOfBirth',
      'firstName',
      'lastName',
      'paymentReference',
      'transferDestination',
    ],
  })

  useEffect(() => {
    if (record) {
      const { beneficiary: beneficiaryDetails } = record?.details ?? {}

      setValue('accountNumber', beneficiaryDetails?.bankAccount?.accountNumber)
      setValue('amount', record?.amount?.value)
      setValue('bankCode', beneficiaryDetails?.bankAccount?.bankCode)
      setValue('bankCountry', beneficiaryDetails?.bankAccount?.bankCountry)
      setValue('businessName', beneficiaryDetails?.entity?.name)
      setValue('dateOfBirth', beneficiaryDetails?.entity?.dateOfBirth)
      setValue('firstName', beneficiaryDetails?.entity?.firstName)
      setValue('lastName', beneficiaryDetails?.entity?.lastName)
      setValue('paymentReference', record?.paymentReference)
      setValue('transferDestination', beneficiaryDetails?.transferDestination)
      setValue('type', beneficiaryDetails?.type)
    }
  }, [record])

  const isB2CPayout = useMemo(() => {
    return isFiatB2CPayment({ firstName, lastName, dateOfBirth, name: businessName })
  }, [firstName, lastName, dateOfBirth, businessName])

  const { data: countries, isPending: isCountriesLoading } = useFilteredCountriesQuery(true)
  const [generalError, setGeneralError] = useState('')
  const [paymentDisabled, setPaymentDisabled] = useState(false)

  useSetFiatRecordToEditErrors({
    record,
    walletCurrency,
    setError,
    setGeneralError,
    setPaymentDisabled,
  })

  const countryOptions = useMemo(() => {
    if (countries?.length) {
      return mapToItems(countries)
    }

    return []
  }, [countries])

  useEffect(() => {
    if (countryOptions?.length && bankCountry) {
      const bankCountryFound = countryOptions?.find(country => country?.value === bankCountry)

      if (!bankCountryFound) {
        setValue('bankCountry', '')
        setError?.('bankCountry', { message: t('massPayouts.fiatErrors.bankCountry') })
      }
    }
  }, [countryOptions, bankCountry])

  useEffect(() => {
    if (transferDestination && transferDestination !== BeneficiaryTransferDestination.LOCAL) {
      setValue('transferDestination', '')
      setError?.('transferDestination', {
        message: t('massPayouts.fiatErrors.transferDestination'),
      })
    }
  }, [transferDestination])

  const isPayoutUpdateFormDisabled = useMemo(() => {
    if (isB2CPayout) {
      return (
        !bankCode ||
        !accountNumber ||
        !amount ||
        !paymentReference ||
        !bankCountry ||
        !firstName ||
        !lastName ||
        !dateOfBirth
      )
    } else {
      return (
        !businessName || !bankCode || !accountNumber || !amount || !paymentReference || !bankCountry
      )
    }
  }, [
    isB2CPayout,
    bankCode,
    accountNumber,
    amount,
    paymentReference,
    bankCountry,
    firstName,
    lastName,
    dateOfBirth,
  ])

  const onBeneficiaryTypeSelection = (beneficiaryType: BeneficiaryType) => {
    setValue('type', beneficiaryType)
  }

  const onTransferDestinationSelection = (type: BeneficiaryTransferDestination) => {
    setValue('transferDestination', type)
  }

  const getBeneficiaryTypeDetails = (isB2C: boolean) => {
    if (isB2C) {
      return (
        <>
          <Box flex gapX={12} width="full">
            <Box flexSize="auto" width="full">
              <Input
                {...register('firstName')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue('firstName', e.target.value)
                }
                label={t('formFields.firstName')}
                placeholder={t('enterName')}
                inputMode="text"
                name="firstName"
                testId="first-name"
                errorText={formState?.errors?.firstName?.message}
                error={!!formState?.errors?.firstName}
                value={firstName}
              />
            </Box>
            <Box flexSize="auto" width="full">
              <Input
                {...register('lastName')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue('lastName', e.target.value)
                }
                label={t('formFields.lastName')}
                placeholder={t('enterName')}
                inputMode="text"
                name="lastName"
                testId="last-name"
                errorText={formState?.errors?.lastName?.message}
                error={!!formState?.errors?.lastName}
                value={lastName}
              />
            </Box>
          </Box>
          <Box>
            <Input
              {...register('dateOfBirth')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('dateOfBirth', e.target.value)
              }
              label={t('formFields.dateOfBirth')}
              placeholder={t('formFields.dateOfBirthFormat')}
              inputMode="text"
              name="dateOfBirth"
              testId="date-of-birth"
              errorText={formState?.errors?.dateOfBirth?.message}
              error={!!formState?.errors?.dateOfBirth}
              value={dateOfBirth}
            />
          </Box>
        </>
      )
    } else {
      return (
        <Box>
          <Input
            testId="update-business-name-input"
            {...register('businessName')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setValue('businessName', e.target.value)
            }
            label={t('massPayouts.beneficiaryName')}
            inputMode="text"
            name="businessName"
            value={businessName}
            error={!!formState?.errors?.businessName}
            errorText={formState?.errors?.businessName?.message}
          />
        </Box>
      )
    }
  }

  const onUpdatePreProcessedRecordRequest = async () => {
    const commonFields: UpdateFiatPayoutFormPayloadKeys[] = [
      'bankCode',
      'accountNumber',
      'amount',
      'paymentReference',
      'bankCountry',
      'transferDestination',
      'type',
    ]

    const validationFields: UpdateFiatPayoutFormPayloadKeys[] = isB2CPayout
      ? [...commonFields, 'firstName', 'lastName', 'dateOfBirth']
      : [...commonFields, 'businessName']

    const isValidDetails = await trigger(validationFields)

    if (isValidDetails) {
      clearErrors()
      const payload = getEditBeneficiaryPayload({
        businessName,
        bankCode,
        accountNumber,
        amount,
        paymentReference,
        bankCountry,
        firstName,
        lastName,
        dateOfBirth,
        type,
        transferDestination,
        beneficiaryDetails,
        recordReference: record?.reference,
      })
      mutateUpdatePreProcessedRecord(payload, {
        onSuccess: () => {
          setIsEditPayoutModalVisible(false)
          queryClient.invalidateQueries({ queryKey: ['massFiatPayoutsDetails'] })
          queryClient.invalidateQueries({ queryKey: ['massFiatPayoutsRecords'] })
          setPreProcessedRecordToUpdate(null)
          toast({
            header: t('massPayouts.paymentUpdated'),
            variant: 'success',
          })
        },
        onError: (error: BaseErrorResponse) => {
          toast({
            header: t('oopsSomethingWentWrong'),
            variant: 'error',
          })
          const bvknErrorStatus = error?.status
          if (bvknErrorStatus === 400) {
            // Set appropriate form error message if validation fails.
          } else {
            // Display error message sent from the BE.
          }
        },
      })
    }
  }

  return (
    <>
      <Box paddingX={12} className="my-4 space-y-6 text-gray-100">
        {!!generalError && <Callout variant="error">{generalError}</Callout>}
        <Box>
          <Radio
            {...register('type')}
            name="type"
            horizontal={true}
            label={t('manageBeneficiaries.beneficiaryType')}
            onChange={onBeneficiaryTypeSelection}
            options={[
              {
                label: t('internal'),
                value: BeneficiaryType.FIRST_PARTY,
              },
              {
                label: t('external'),
                value: BeneficiaryType.THIRD_PARTY,
              },
            ]}
            value={type}
            error={formState?.errors?.type?.message}
          />
        </Box>
        {getBeneficiaryTypeDetails(isB2CPayout)}
        <Input
          testId="update-bank-code-input"
          {...register('bankCode')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('bankCode', e.target.value)
          }
          label={t('massPayouts.bankCode')}
          inputMode="text"
          name="bankCode"
          value={bankCode}
          error={!!formState?.errors?.bankCode}
          errorText={formState?.errors?.bankCode?.message}
        />
        <Input
          testId="update-account-number-input"
          {...register('accountNumber')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('accountNumber', e.target.value)
          }
          label={t('massPayouts.accountNumber')}
          inputMode="text"
          name="accountNumber"
          value={accountNumber}
          error={!!formState?.errors?.accountNumber}
          errorText={formState?.errors?.accountNumber?.message}
        />
        <Box>
          <Radio
            {...register('transferDestination')}
            name="transferDestination"
            horizontal={true}
            label={t('massPayouts.beneficiaryLocation')}
            onChange={onTransferDestinationSelection}
            options={[
              {
                label: t('manageBeneficiaries.local'),
                value: BeneficiaryTransferDestination.LOCAL,
              },
              {
                label: t('manageBeneficiaries.international'),
                value: BeneficiaryTransferDestination.INTERNATIONAL,
                disabled: true,
              },
            ]}
            value={transferDestination}
            error={formState?.errors?.transferDestination?.message}
          />
        </Box>
        <Combobox
          {...register('bankCountry')}
          label={t('bankCountry')}
          defaultValue={bankCountry}
          items={countryOptions}
          errorText={formState?.errors?.bankCountry?.message}
          selectPlaceholder={t('searchOrSelectACountry')}
          loading={isCountriesLoading}
          onChange={(countryCode: string) => {
            setValue('bankCountry', countryCode)
          }}
          id="bankCountry"
        />
        <Input
          testId="update-amount-input"
          {...register('amount')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('amount', e.target.value)}
          label={t('massPayouts.amount')}
          inputMode="text"
          name="amount"
          value={amount}
          error={!!formState?.errors?.amount}
          errorText={formState?.errors?.amount?.message}
        />
        <Input
          testId="update-reference-input"
          {...register('paymentReference')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue('paymentReference', e.target.value)
          }
          label={t('massPayouts.paymentReference')}
          inputMode="text"
          name="paymentReference"
          value={paymentReference}
          error={!!formState?.errors?.paymentReference}
          errorText={formState?.errors?.paymentReference?.message}
        />
        <Box flex justify={paymentDisabled ? 'end' : 'between'}>
          <Button
            testId="remove-from-batch"
            variant="secondary"
            loading={isDeletingPreProcessedRecord}
            onClick={() => onDeletePreProcessedRecordRequest(record?.reference)}
          >
            {t('massPayouts.removeFromBatch')}
          </Button>
          {!paymentDisabled && (
            <Button
              testId="update-payout"
              loading={isLoadingUpdatePreProcessedRecord}
              disabled={isPayoutUpdateFormDisabled}
              onClick={onUpdatePreProcessedRecordRequest}
            >
              {t('massPayouts.update')}
            </Button>
          )}
        </Box>
      </Box>
    </>
  )
}
